import React from "react"
import { Platform } from "react-native"

import * as Sentry from "@sentry/react-native"
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query"
import { usePostHog } from "posthog-react-native"
import { useMMKVBoolean, useMMKVObject } from "react-native-mmkv"
import { TConversation, TMessage } from "types/transcript"
import { StartConversationMessageSchema, WebsocketStartConversationMessage } from "types/websocket"
import { z } from "zod/v4"

import { useAuth } from "src/context/AuthContext"
import { useStores } from "src/models/helpers/useStores"
import { Api } from "src/services/api"
import { delay } from "src/utils/delay"
import { computeMessageKey } from "src/utils/messageUtils"
// Conversation history is per-tab so different tabs can hold separate
// conversations against different sponsors/roles. The `storage` alias
// here points at `sessionScopedStorage` (sessionStorage on web via MMKV's
// `webStorage: "session"` patch) so every existing `storage.*` call in
// this file becomes per-tab without touching call sites. Native still has
// a single tab; the namespace is just a separate MMKV instance.
import { sessionScopedStorage as storage } from "src/utils/storage/mmkv"
import { getCurrentSponsor } from "./useConfig"

export const CHAT_CURRENT_CONVERSATION_KEY = "chat.currentConversation"
export const CHAT_PAST_CONVERSATIONS_KEY = "chat.pastConversations"
/** When we archive current on start_conversation (transfer), keep it here until server past includes it (sync can overwrite past). */
export const CHAT_LAST_CONVERSATION_KEY = "chat.lastConversation"
/** Conversations just archived but not yet in server past; sync can overwrite past so we preserve them here. */
export const CHAT_RECENTLY_CLOSED_CONVERSATIONS_KEY = "chat.recentlyClosedConversations"
export const CHAT_HISTORY_DIAGNOSTIC_KEY = "chat.historyDiagnostic"
export const CHAT_SYNC_STATUS_KEY = "chat.synchronizeAllConversationsFromQueryStatus"
// Flag to track if we need to sync conversations this session (reset on login/clear)
export const CHAT_NEEDS_SYNC_KEY = "chat.needsSync"

// When 404 + local current not in past: backend may take ~30s to add the conversation to past (race at close).
// We re-sync after a short delay to catch when it appears; no need to wait 30s before requesting.
const DELAYED_SYNC_MS = 5_000

/** Deduplicate transcript by message_id+role so we never persist duplicates (e.g. after long sleep + sync). */
function deduplicateTranscript(transcript: TMessage[]): TMessage[] {
  const seen = new Set<string>()
  return transcript.filter((m) => {
    if (m.message_id == null || m.role == null) return true // keep messages without stable key
    const key = computeMessageKey(m)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}
let delayedSyncTimeoutId: ReturnType<typeof setTimeout> | null = null

// ---------------------------------------------------------------------------
// Conversation history pagination state
//
// The initial sync (synchronizeAllConversationsFromQuery) always fetches page 1
// of conversation summaries. When the user scrolls to the oldest visible
// message, SidekickChat calls fetchMorePastConversations() to fetch subsequent
// pages and append them to an in-memory React Query cache (so they are never
// persisted to localStorage and therefore never hit the web quota cap).
// ---------------------------------------------------------------------------
let _conversationsNextPage = 2
let _hasMoreConversations = true
let _isFetchingMoreConversations = false

/** True if the server has more conversation history pages beyond what is loaded. */
export function getConversationsHasMore(): boolean {
  return _hasMoreConversations
}

function _resetConversationPagination() {
  _conversationsNextPage = 2
  _hasMoreConversations = true
  _isFetchingMoreConversations = false
}

/**
 * Fetches the next page of past conversations from the server and appends them
 * to the in-memory React Query cache key ["conversations", "extended-past"].
 * Safe to call repeatedly; no-ops when already fetching or when the server has
 * indicated there are no more pages.
 */
export async function fetchMorePastConversations(
  api: Api,
  queryClient: QueryClient,
): Promise<void> {
  if (_isFetchingMoreConversations || !_hasMoreConversations) return
  _isFetchingMoreConversations = true

  try {
    const page = _conversationsNextPage
    const response = await api.getConversationsSummaries(page)

    if (response.kind !== "ok") {
      if (__DEV__) {
        console.warn(`[fetchMorePastConversations] Summaries page ${page} failed:`, response.kind)
      }
      return
    }

    const { summaries, hasMore } = response

    if (summaries.length === 0) {
      _hasMoreConversations = false
      return
    }

    // Determine which summaries are genuinely new (not already in MMKV or extended cache).
    const existingExtended =
      queryClient.getQueryData<TConversation[]>(["conversations", "extended-past"]) ?? []
    const existingMmkv = getPastConversations()
    const existingIds = new Set<string>(
      [...existingMmkv.map((c) => c?.id), ...existingExtended.map((c) => c?.id)].filter(
        (id): id is string => !!id,
      ),
    )

    const newSummaries = summaries.filter((s) => !existingIds.has(s.id))
    if (newSummaries.length > 0) {
      const newConversations = (
        await Promise.all(
          newSummaries.map((s) =>
            queryClient.fetchQuery({
              queryKey: ["conversations", s.id],
              queryFn: getConversationById(api, s.id),
              staleTime: 0,
            }),
          ),
        )
      ).filter((c): c is TConversation => c !== null)

      if (newConversations.length > 0) {
        queryClient.setQueryData<TConversation[]>(
          ["conversations", "extended-past"],
          [...existingExtended, ...newConversations],
        )
      }
    }

    _conversationsNextPage = page + 1
    _hasMoreConversations = hasMore && summaries.length > 0
  } finally {
    _isFetchingMoreConversations = false
  }
}

// Set by the conversation query functions whenever a 401 reaches them
// (session-level failure — typically SESSION_DEACTIVATED or
// CONTEXT_TOKEN_INVALID against a stale tab). Read by
// synchronizeAllConversationsFromQuery to short-circuit the delayed re-sync
// path; otherwise the synchronizer would interpret the gracefully-bailed
// `null` as a true 404 and reschedule itself every DELAYED_SYNC_MS,
// hammering the backend with 401s while the takeover dialog is up.
let _sessionLevelBailObservedDuringSync = false

function markConversationFetchUnauthorized() {
  _sessionLevelBailObservedDuringSync = true
}

// We detect previous conversation closed when the poll results in a missing conversation
// or a different conversation.
export async function pollForConversationEnd(
  currentConversationQuery: UseQueryResult<TConversation | null, unknown>,
) {
  const lastConversationId = currentConversationQuery.data?.id

  const maxPollTime = 60 * 1000
  const startTime = Date.now()
  while (true) {
    const currentConversation = (await currentConversationQuery.refetch()).data
    const hasCurrentConversation = currentConversation !== null
    const isConversationIdChanged = currentConversation?.id !== lastConversationId

    console.log(
      "pollForConversationEnd: hasCurrentConversation:",
      hasCurrentConversation,
      "currentConversationId:",
      currentConversation?.id,
      "lastConversationId:",
      lastConversationId,
    )

    if (!hasCurrentConversation || isConversationIdChanged) {
      break
    }

    await delay(500)

    if (Date.now() - startTime > maxPollTime) {
      throw new Error("pollForConversationEnd: Conversation not closed after 60 seconds")
    }
  }

  return currentConversationQuery.data
}

type ChatHistoryDiagnostic = {
  type: "closeCurrentConversation" | "synchronizeOnAppActive"
  queryTranscript: TMessage[] | undefined
  mmkvTranscript: TMessage[] | undefined
}

// FIXME: what is the right pattern to resolve the issue of hook coloring?
// Some actions need to be taken by callbacks or non-react-render-tree code ...
// But it's really unfortunate to need to make hook and non-hook mirrors of the same
// basic logic.
function pushChatHistoryDiagnostic(diagnostic: ChatHistoryDiagnostic) {
  const chatHistoryJson = storage.getString(CHAT_HISTORY_DIAGNOSTIC_KEY)
  const chatHistory = chatHistoryJson ? JSON.parse(chatHistoryJson) : []
  chatHistory.push(diagnostic)
  storage.set(CHAT_HISTORY_DIAGNOSTIC_KEY, JSON.stringify(chatHistory))
}

export function useChatHistoryDiagnostic() {
  const [chatHistoryDiagnostic, setChatHistoryDiagnostic] = useMMKVObject<ChatHistoryDiagnostic[]>(
    CHAT_HISTORY_DIAGNOSTIC_KEY,
    storage,
  )

  const _pushChatHistoryDiagnostic = (diagnostic: ChatHistoryDiagnostic) => {
    setChatHistoryDiagnostic((prev) => [...(prev || []), diagnostic])
  }

  const _clearChatHistoryDiagnostic = () => {
    setChatHistoryDiagnostic(undefined)
  }

  return {
    chatHistoryDiagnostic,
    pushChatHistoryDiagnostic: _pushChatHistoryDiagnostic,
    clearChatHistoryDiagnostic: _clearChatHistoryDiagnostic,
  }
}

export function useCloseCurrentConversation() {
  const posthog = usePostHog()
  const { api } = useAuth()
  const queryClient = useQueryClient()
  const currentConversationQuery = useCurrentConversationQuery()
  const { currentConversation: currentConversationMmkv } = useCurrentConversationMmkv()
  const { pushChatHistoryDiagnostic } = useChatHistoryDiagnostic()
  const { pastConversations, setPastConversations } = usePastConversations()

  return useMutation({
    mutationFn: async () => {
      // We don't query the current conversation normally, so we need to refetch it before we close it
      // so we can use it to check the consistency of our local cache
      console.log("useCloseCurrentConversation: Closing current conversation")
      const currentConversationRefetch = (await currentConversationQuery.refetch()).data

      pushChatHistoryDiagnostic({
        type: "closeCurrentConversation",
        queryTranscript: currentConversationRefetch?.transcript,
        mmkvTranscript: currentConversationMmkv?.transcript,
      })

      const response = await api.closeCurrentConversation()
      if (response.kind !== "ok") {
        throw new Error("useCloseCurrentConversation: Failed to close conversation")
      }

      const noConversationWasOpen = response.result === false

      if (noConversationWasOpen) {
        // 409 was treated as success: server had no conversation to close. Sync local state
        // so SidekickChat's effect can start a new conversation (same as sync 404 + no local).
        setCurrentConversation(undefined)
        queryClient.setQueryData(["conversations", "current"], null)
        queryClient.setQueryData(["startConversationStatus"], { isPending: false })
        return response.result
      }

      posthog?.capture("conversation_closed", {
        conversationId: currentConversationRefetch?.id ?? null,
        skPlatform: "app",
      })

      await pollForConversationEnd(currentConversationQuery)
      console.log(
        "useCloseCurrentConversation: Previous conversation closed. Removing cached current conversation query.",
      )

      // Our current conversation has ended, so remove it from the current conversation cache.
      // Then insert the just ended conversation into the past conversations cache and into
      // recentlyClosedConversations so it stays visible until the server includes it in past.
      if (currentConversationMmkv) {
        // Clear our local caches of the current conversation now that it's closed
        setCurrentConversation(undefined)
        queryClient.setQueryData(["conversations", "current"], null)
        addRecentlyClosedConversation(currentConversationMmkv)
        setPastConversations([currentConversationMmkv, ...pastConversations])
      } else {
        console.error(
          "useCloseCurrentConversation: No current conversation to cache. Conversation dropped from history.",
        )
      }

      return response.result
    },
    onMutate: async () => {
      // Update a shared query's state or cache to indicate mutation start
      queryClient.setQueryData(["closeConversationStatus"], { isPending: true })
    },
    onSettled: () => {
      // Update a shared query's state or cache to indicate mutation end
      queryClient.setQueryData(["closeConversationStatus"], { isPending: false })
    },
    onError: (error: Error) => {
      console.error("useCloseCurrentConversation: Error closing conversation:", error.message)
    },
  })
}

export function useCloseConversationStatus() {
  return useQuery({
    queryKey: ["closeConversationStatus"],
    queryFn: () => ({ isPending: false }),
    initialData: { isPending: false },
  })
}

export function useStartConversationStatus() {
  return useQuery({
    queryKey: ["startConversationStatus"],
    queryFn: () => ({ isPending: false }),
    initialData: { isPending: false },
  })
}

export function useConversationRejectedStatus() {
  return useQuery({
    queryKey: ["conversationRejectedStatus"],
    queryFn: () => ({ isRejected: false }),
    initialData: { isRejected: false },
  })
}

export function getSynchronizeAllConversationsFromQueryStatus(): boolean {
  return storage.getBoolean(CHAT_SYNC_STATUS_KEY) || false
}

export function setSynchronizeAllConversationsFromQueryStatus(isSynchronizing: boolean) {
  storage.set(CHAT_SYNC_STATUS_KEY, isSynchronizing)
}

// Track whether we need to sync conversations this session
// This is set to true on login/clear and false after sync completes
export function getNeedsSyncStatus(): boolean {
  // Default to true so we sync on first app launch
  const value = storage.getBoolean(CHAT_NEEDS_SYNC_KEY)
  return value === undefined ? true : value
}

export function setNeedsSyncStatus(needsSync: boolean) {
  storage.set(CHAT_NEEDS_SYNC_KEY, needsSync)
}

export function useSynchronizeAllConversationsFromQueryStatus() {
  const [isSynchronizing, setIsSynchronizing] = useMMKVBoolean(CHAT_SYNC_STATUS_KEY, storage)

  return {
    isSynchronizing: isSynchronizing || false,
    setIsSynchronizing,
  }
}

export function getConversationById(api: Api, conversationId: string) {
  return async () => {
    const response = await api.getConversationById(conversationId)
    if (response.kind === "unauthorized") {
      // A 401 reaching the queryFn means session-level failure (most
      // commonly SESSION_DEACTIVATED — another tab/device claimed the session).
      // The takeover dialog (AuthContext.isSessionDeactivated) handles user
      // remediation; we bail gracefully so we don't surface a redundant
      // "Failed to fetch …" error overlay behind the dialog. resumeSession
      // invalidates queries on success, so this query refetches with the
      // fresh token.
      console.log("getConversationById: session-level 401, bailing for dialog")
      markConversationFetchUnauthorized()
      return null
    }
    if (response.kind !== "ok") {
      throw new Error("Failed to fetch conversation")
    }
    return response.conversation
  }
}

export function getConversationSummaries(api: Api) {
  return async () => {
    const response = await api.getConversationsSummaries()
    if (response.kind === "unauthorized") {
      // See getConversationById for rationale.
      console.log("getConversationSummaries: session-level 401, bailing for dialog")
      markConversationFetchUnauthorized()
      return []
    }
    if (
      response.kind === "canceled" ||
      response.kind === "cannot-connect" ||
      response.kind === "timeout"
    ) {
      // Transient failure — request was cancelled or the network was briefly
      // unavailable (common when a tab is foregrounded after being backgrounded).
      // Return an empty list rather than throwing so callers don't surface an
      // error overlay; the next sync cycle will retry.
      console.log(`getConversationSummaries: transient ${response.kind}, returning []`)
      return []
    }
    if (response.kind !== "ok") {
      throw new Error("Failed to fetch conversations")
    }
    return response.summaries
  }
}

function getCurrentConversationQuery(api: Api) {
  return async () => {
    const response = await api.getCurrentConversation()

    if (response.kind === "not-found") {
      // Could not find a current conversation in the backend
      console.log("getCurrentConversationQuery: Conversation not found")
      return null
    }

    if (response.kind === "unauthorized") {
      // A 401 reaching the queryFn means session-level failure (most
      // commonly SESSION_DEACTIVATED — another tab/device claimed the session).
      // The takeover dialog (AuthContext.isSessionDeactivated) handles user
      // remediation; we bail gracefully so we don't surface a redundant
      // "Failed to fetch current conversation" error overlay behind the dialog.
      // resumeSession invalidates queries on success, so this query refetches
      // with the fresh token.
      console.log("getCurrentConversationQuery: session-level 401, bailing for dialog")
      markConversationFetchUnauthorized()
      return null
    }

    if (response.kind !== "ok") {
      console.error("getCurrentConversationQuery:", response)
      throw new Error("Failed to fetch current conversation")
    }

    if (response.conversation?.status?.toLowerCase() === "ended") {
      // 'current' conversation has ended
      // there is no current conversation, even though the API returns the last conversation (it arguably shouldn't)
      console.log("getCurrentConversationQuery: Conversation ended")
      return null
    }

    console.log(
      "getCurrentConversationQuery: Returning fetched conversation",
      response.conversation?.id,
    )
    return response.conversation
  }
}

function useCurrentConversationQuery() {
  const { api, isAuthenticated, pendingLoginSponsors } = useAuth()

  return useQuery({
    queryFn: getCurrentConversationQuery(api),
    queryKey: ["conversations", "current"],

    // Only fetch once authenticated and after a context token has been acquired
    // (i.e. not while the user is still picking a sponsor after multi-sponsor login).
    // pendingLoginSponsors !== null is the "awaiting sponsor selection" signal —
    // skipping here keeps /conversations/current from firing on the SelectSponsor
    // screen and tripping the takeover dialog with a stale/missing context token.
    enabled: !!api && isAuthenticated && pendingLoginSponsors === null,

    // Don't retry on error:
    // If there is no current conversation, the server may return an error.
    // We need to take action (e.g. starting a new conversation) before we'll be able to query the current conversation
    // so retrying automatically would be a mistake.
    retry: false,
    // override the default staleTime for this query - Infinity means data will never automatically become stale
    staleTime: Infinity,
  })
}

export function useCurrentConversationMmkv() {
  const [currentConversation, setCurrentConversation] = useMMKVObject<TConversation>(
    CHAT_CURRENT_CONVERSATION_KEY,
    storage,
  )

  // FIXME: MMKV uses undefined but all the components that work with query data expect null when missing
  return { currentConversation: currentConversation || null, setCurrentConversation }
}

// Synchronize the current and past conversations from REST backend,
// then merge that data with our local cache (while checking for consistency and deletions).
//
// Unfortunately this can't easily be a hook. We want to procedurally call these API endpoints
// and use the results for later calls, which doesn't suit well to hooks, especially when
// querying several conversations by Id.
export async function synchronizeAllConversationsFromQuery(api: Api, queryClient: QueryClient) {
  // Set that we have initiated sync
  setSynchronizeAllConversationsFromQueryStatus(true)

  // Each full sync starts fresh from page 1, so reset pagination state and
  // drop any previously loaded extended-past pages so they are refetched if
  // the user scrolls back again.
  _resetConversationPagination()
  queryClient.setQueryData(["conversations", "extended-past"], [])

  // Reset the session-level bail signal at the start of each sync so we only
  // observe 401s that occurred during THIS run (not a leftover from the
  // previous sync). See markConversationFetchUnauthorized() above.
  _sessionLevelBailObservedDuringSync = false

  try {
    // Fetch all our conversation data from REST
    const currentConversationRest = await queryClient.fetchQuery({
      queryKey: ["conversations", "current"],
      queryFn: getCurrentConversationQuery(api),
      // Used to force refetch if already cached
      staleTime: 0,
    })

    const summariesRest = await queryClient.fetchQuery({
      queryKey: ["conversations", "summaries"],
      queryFn: getConversationSummaries(api),
      staleTime: 0,
    })

    // FIXME: this is an N+1 query and it would be better to have a batch endpoint
    const pastConversationsRest = await Promise.all(
      summariesRest.map(async (summary) => {
        const conversation = await queryClient.fetchQuery({
          queryKey: ["conversations", summary.id],
          queryFn: getConversationById(api, summary.id),
          staleTime: 0,
        })
        return conversation
      }),
    )

    const currentConversationMmkv = getCurrentConversation()

    // Record our local history and the REST history for diagnostic comparison
    pushChatHistoryDiagnostic({
      type: "synchronizeOnAppActive",
      queryTranscript: currentConversationRest?.transcript,
      mmkvTranscript: currentConversationMmkv?.transcript,
    })

    // Guard: if the server's "current" is already in our local past, the user has already
    // intentionally closed it and started a new conversation (e.g. from the content library).
    // The server may not have caught up yet. Restoring it as current would duplicate every
    // message it shares with the archived copy. Skip the update and let the new conversation
    // arrive via updateConversationFromStartMessage.
    const localPastConversations = getPastConversations()
    const isRestCurrentAlreadyArchivedLocally =
      !!currentConversationRest &&
      localPastConversations.some((c) => c?.id === currentConversationRest.id)

    // We check the staleness of the REST current conversation against our local cache, and choose which to use
    if (!currentConversationRest) {
      // Server returned no current conversation (e.g. 404)
      const isLocalInPast =
        currentConversationMmkv &&
        pastConversationsRest.some((past) => past?.id === currentConversationMmkv.id)

      if (__DEV__) {
        console.warn("[sync] conversations/current returned 404", {
          hasLocalCurrent: !!currentConversationMmkv,
          localCurrentId: currentConversationMmkv?.id,
          isLocalInPast,
          pastCount: pastConversationsRest.length,
        })
      }

      if (!currentConversationMmkv) {
        // No local current: clear so SidekickChat starts a new conversation
        if (__DEV__) {
          console.warn(
            "[sync] 404 + no local current: clearing current so start_conversation can run",
          )
        }
        setCurrentConversation(undefined)
      } else if (isLocalInPast) {
        // Local current is already in server past: safe to clear so SidekickChat starts a new conversation
        if (__DEV__) {
          console.warn(
            "[sync] 404 + local current is in past: clearing current so start_conversation can run",
            { conversationId: currentConversationMmkv.id },
          )
        }
        setCurrentConversation(undefined)
      } else {
        // Local current not in past yet. Determine whether to schedule a re-sync or fall through.
        const startConversationStatus = queryClient.getQueryData<{ isPending: boolean }>([
          "startConversationStatus",
        ])
        const conversationRejectedStatus = queryClient.getQueryData<{ isRejected: boolean }>([
          "conversationRejectedStatus",
        ])

        if (startConversationStatus?.isPending) {
          // A start_conversation is in-flight. The incoming WS event (start_conversation or
          // ERR-32) will handle currentConversation, so scheduling a re-sync loop here would
          // spin forever (the temp's numeric ID never appears in server past).
          // NOTE: isPending = true and isConversationRejected = true are mutually exclusive —
          // isPending is always cleared before isConversationRejected is set in the WS handler.
          // Do NOT return — fall through so past conversations are still updated now.
          if (__DEV__) {
            console.warn(
              "[sync] 404 + start_conversation pending — temp placeholder in MMKV, skipping re-sync loop, updating past conversations",
              { conversationId: currentConversationMmkv.id },
            )
          }
        } else if (_sessionLevelBailObservedDuringSync) {
          // A sub-fetch bailed on a 401 — this is a session-level failure, not a true 404.
          // Scheduling a re-sync would loop forever against the displaced session. Bail
          // entirely; the takeover dialog's "Resume Here" flow will invalidate queries.
          if (__DEV__) {
            console.warn(
              "[sync] 404 was a session-level 401 bail — skipping re-sync schedule (dialog should be up)",
              { conversationId: currentConversationMmkv.id },
            )
          }
          return
        } else if (conversationRejectedStatus?.isRejected) {
          // Server already rejected this conversation (e.g. ERR-32 trial ended).
          // No re-sync will produce a current conversation.
          // Do NOT return — fall through so past conversations are still updated.
          if (__DEV__) {
            console.warn(
              "[sync] 404 + conversation rejected (e.g. ERR-32) — skipping re-sync loop, updating past conversations",
              { conversationId: currentConversationMmkv.id },
            )
          }
        } else {
          // APP-622: Real conversation that the server hasn't added to past yet (race at close).
          // Re-sync after a short delay.
          if (__DEV__) {
            console.warn(
              "[sync] 404 + local current not in past: keeping MMKV, scheduling re-sync in",
              DELAYED_SYNC_MS,
              "ms",
              { conversationId: currentConversationMmkv.id },
            )
          }
          Sentry.captureMessage("Current conversation not found in REST or past conversations", {
            tags: { issue: "APP-622" },
            extra: {
              currentConversationId: currentConversationMmkv.id,
              pastConversationIds: pastConversationsRest.map((c) => c?.id),
            },
          })
          if (delayedSyncTimeoutId !== null) {
            clearTimeout(delayedSyncTimeoutId)
            delayedSyncTimeoutId = null
          }
          delayedSyncTimeoutId = setTimeout(() => {
            delayedSyncTimeoutId = null
            if (__DEV__) {
              console.warn(
                "[sync] Running delayed re-sync after 404 (conversation may be in past now)",
              )
            }
            synchronizeAllConversationsFromQuery(api, queryClient).catch((e) => {
              console.warn("[sync] delayed re-sync rejected", e)
            })
          }, DELAYED_SYNC_MS)
        }
      }
    } else if (isRestCurrentAlreadyArchivedLocally) {
      // The REST current is already in our local past — a user-initiated switch is in flight
      // (e.g. content library selection). Leave the local current (temp or undefined) in place
      // so the arriving start_conversation event can set the real new conversation.
      if (__DEV__) {
        console.warn(
          "[sync] Skipping: REST current is already in local past (race window after user-initiated switch)",
          currentConversationRest.id,
        )
      }
    } else if (
      currentConversationMmkv &&
      currentConversationRest.id === currentConversationMmkv.id &&
      currentConversationRest.transcript.length >= currentConversationMmkv.transcript.length
    ) {
      // Same conversation, REST has at least as many messages — REST wins.
      setCurrentConversation({
        ...currentConversationRest,
        transcript: deduplicateTranscript(currentConversationRest.transcript || []),
      } as TConversation)
    } else if (
      currentConversationMmkv &&
      currentConversationRest.id === currentConversationMmkv.id
    ) {
      // Same conversation, but local has MORE messages than REST. This happens with
      // suppress_first_message flows (C2-2210): the backend sends a PlayVideo event but
      // does not persist it to the conversation transcript, so REST always returns an
      // empty or shorter transcript. Keep the local version — MMKV wins.
      // (No-op: do not call setCurrentConversation.)
    } else if (
      currentConversationMmkv &&
      currentConversationRest.id !== currentConversationMmkv.id
    ) {
      // Different conversation ID — local cache is stale, use REST.
      setCurrentConversation({
        ...currentConversationRest,
        transcript: deduplicateTranscript(currentConversationRest.transcript || []),
      } as TConversation)
    } else {
      // No local current conversation — seed from REST.
      setCurrentConversation({
        ...currentConversationRest,
        transcript: deduplicateTranscript(currentConversationRest.transcript || []),
      } as TConversation)
    }

    // If our REST past conversations history is at least as long as our local history, use our REST history.
    // Preserve recentlyClosedConversations (just-archived, not yet in server past) until server includes them.
    // Deduplicate each conversation's transcript so we never persist duplicates (e.g. after long sleep).
    if (pastConversationsRest.length >= localPastConversations.length) {
      const recentlyClosed = getRecentlyClosedConversations()
      const serverPastIds = new Set(pastConversationsRest.map((c) => c?.id).filter(Boolean))
      const stillNotInServer = recentlyClosed.filter((c) => !serverPastIds.has(c.id))
      setRecentlyClosedConversations(stillNotInServer)
      let newPast = (pastConversationsRest as TConversation[]).map((c) =>
        c ? { ...c, transcript: deduplicateTranscript(c.transcript || []) } : c,
      )
      if (stillNotInServer.length > 0) {
        newPast = [...stillNotInServer, ...newPast]
      }
      setPastConversations(newPast)

      // On web, setPastConversations caps the persisted list to MAX_CACHED_PAST_CONVERSATIONS
      // to stay within the localStorage quota. Any conversations beyond that cap are stored
      // in the in-memory React Query cache so they still appear in the chat history without
      // consuming localStorage space.
      if (Platform.OS === "web" && newPast.length > MAX_CACHED_PAST_CONVERSATIONS) {
        const overflow = newPast.slice(MAX_CACHED_PAST_CONVERSATIONS)
        queryClient.setQueryData<TConversation[]>(["conversations", "extended-past"], overflow)
        // All conversations are now in memory — no server pagination needed.
        _hasMoreConversations = false
      }
    }

    if (__DEV__) {
      console.warn("synchronizeAllConversationsFromQuery: Refreshed conversation data")
    }

    // Mark that we've completed sync successfully
    setNeedsSyncStatus(false)
  } catch (e) {
    // CancelledError is thrown when removeQueries cancels an in-flight fetchQuery,
    // e.g. during a sponsor/role switch that calls clearCachedConversations.
    // This is an expected, benign race — abandon the sync run silently.
    const isCancelled =
      e != null &&
      typeof e === "object" &&
      ((e as { name?: string }).name === "CancelledError" ||
        (e as { message?: string }).message === "CancelledError")
    if (!isCancelled) throw e
  } finally {
    // Set that we have completed sync
    setSynchronizeAllConversationsFromQueryStatus(false)
  }
}

export function useCurrentConversation() {
  const { api, isAuthenticated, pendingLoginSponsors } = useAuth()
  const queryClient = useQueryClient()

  const currentConversationQuery = useCurrentConversationQuery()
  const { currentConversation, setCurrentConversation } = useCurrentConversationMmkv()

  // Queue fetch of conversation data if we haven't synced or our conversation cache has been cleared.
  // Must be in useEffect (not the render body) so the side effect never fires during a concurrent
  // render pass — calling synchronizeAllConversationsFromQuery during render causes React to warn
  // "Cannot update a component while rendering a different component" when the async query
  // settlement triggers useSyncExternalStore subscribers mid-render.
  React.useEffect(() => {
    // Do not sync until authenticated and a context token has been acquired.
    // synchronizeAllConversationsFromQuery calls queryClient.fetchQuery with staleTime:0
    // which bypasses the `enabled` flag on useCurrentConversationQuery, so we must
    // guard here too. Crucially, this prevents /conversations/current from being
    // called while the user is still on the select-sponsor screen after multi-sponsor
    // login (pendingLoginSponsors !== null means no context token yet).
    if (!isAuthenticated || pendingLoginSponsors !== null) return
    const isSynchronizing = getSynchronizeAllConversationsFromQueryStatus()
    const needsSync = getNeedsSyncStatus()
    if (needsSync) {
      if (isSynchronizing) {
        console.log("useCurrentConversation: Need sync but already synchronizing")
      } else {
        console.log("useCurrentConversation: Queueing sync")
        // Defensive .catch: synchronizeAllConversationsFromQuery is fire-and-forget
        // here. Any rejection that escapes its own try/catch (which already swallows
        // CancelledError) becomes an unhandled promise rejection — surfacing as an
        // "Uncaught Error" overlay in dev, with a stack pointing at the inner
        // queryFn throw. Log and swallow instead.
        synchronizeAllConversationsFromQuery(api, queryClient).catch((e) => {
          console.warn("useCurrentConversation: synchronize rejected", e)
        })
      }
    }
  })

  // We are splicing our MMKV data and setter over the query fields, this way we get to use existing
  // query lifecycle info if needed, but if not needed we work with the cache data
  return {
    ...currentConversationQuery,
    data: currentConversation,
    setCurrentConversation,
  }
}

// Chat history not including the current conversation
export function getPastConversations(): TConversation[] {
  const conversationsStr = storage.getString(CHAT_PAST_CONVERSATIONS_KEY)
  if (!conversationsStr) return []

  return JSON.parse(conversationsStr) as TConversation[]
}

/** Conversations archived locally but not yet in server past; preserved until sync sees them. */
export function getRecentlyClosedConversations(): TConversation[] {
  const listStr = storage.getString(CHAT_RECENTLY_CLOSED_CONVERSATIONS_KEY)
  if (!listStr) return []
  return JSON.parse(listStr) as TConversation[]
}

/** Add a conversation to the front of recently closed; dedupe by id (keep existing order). */
export function addRecentlyClosedConversation(conversation: TConversation) {
  const list = getRecentlyClosedConversations()
  if (list.some((c) => c?.id === conversation.id)) return
  setRecentlyClosedConversations([conversation, ...list])
}

export function usePastConversations() {
  const [pastConversations, setPastConversations] = useMMKVObject<TConversation[]>(
    CHAT_PAST_CONVERSATIONS_KEY,
    storage,
  )
  const [recentlyClosedConversations] = useMMKVObject<TConversation[]>(
    CHAT_RECENTLY_CLOSED_CONVERSATIONS_KEY,
    storage,
  )

  // Merge recentlyClosedConversations (not yet in server past) into past so they stay visible until sync
  const pastWithRecentlyClosed = React.useMemo(() => {
    const past = pastConversations || []
    const closed = recentlyClosedConversations || []
    if (closed.length === 0) return past
    const pastIds = new Set(past.map((c) => c?.id).filter(Boolean))
    const toPrepend = closed.filter((c) => c?.id && !pastIds.has(c.id))
    if (toPrepend.length === 0) return past
    return [...toPrepend, ...past]
  }, [pastConversations, recentlyClosedConversations])

  return { pastConversations: pastWithRecentlyClosed, setPastConversations }
}

/**
 * Updates the current conversation based on a start_conversation websocket message.
 * This handles both client-initiated and server-initiated (sidekick switch) start_conversation events.
 */
export function updateConversationFromStartMessage(message: WebsocketStartConversationMessage) {
  const parseResult = StartConversationMessageSchema.safeParse(message)
  if (!parseResult.success) {
    console.error(
      "updateConversationFromStartMessage: Invalid start_conversation message",
      z.treeifyError(parseResult.error),
    )
    return
  }

  const {
    conversation_id: conversationId,
    sidekick_avatar_id: sidekickAvatarId,
    sidekick_name: sidekickName,
    esk_sponsor: eskSponsor,
    esk_type: eskType,
    task_id: taskId,
  } = parseResult.data.args

  const currentConversation = getCurrentConversation()

  // Determine if this is a conversation ID change (sidekick transfer)
  const isConversationIdChange = currentConversation && currentConversation.id !== conversationId

  // If conversation ID changed, archive the old conversation to support conversation headers in chat history.
  // Add to recentlyClosedConversations so it stays visible until sync overwrites past with server data (server may not have it yet).
  // Skip archiving when the outgoing conversation is a temp placeholder (set by startConversation); otherwise we
  // would add the temp and the real just-archived conversation could be lost when sync runs.
  if (isConversationIdChange) {
    if (!currentConversation.is_temp) {
      console.log(
        `updateConversationFromStartMessage: New conversation ID (${conversationId}) detected. Archiving old conversation (${currentConversation.id}).`,
      )
      addRecentlyClosedConversation(currentConversation)
      const pastConversations = getPastConversations()
      // Avoid duplicates in past conversations
      if (!pastConversations.some((c) => c.id === currentConversation.id)) {
        setPastConversations([currentConversation, ...pastConversations])
      }
    } else if (__DEV__) {
      console.log(
        `updateConversationFromStartMessage: New conversation ID (${conversationId}) detected. Skipping archive of temp placeholder (${currentConversation.id}).`,
      )
    }
  }

  // If we have an existing conversation with the same ID, update it
  // Otherwise (new conversation or ID changed), create a fresh one
  if (currentConversation && !isConversationIdChange) {
    console.log(
      "updateConversationFromStartMessage: Updating current conversation from start_conversation event",
    )

    const updatedConversation: TConversation = {
      ...currentConversation,
      id: conversationId,
      sidekick_avatar_id: sidekickAvatarId,
      sidekick_name: sidekickName,
      active_esk_sponsor: eskSponsor,
      active_esk_type: eskType,
      active_task_id: taskId,
    }

    setCurrentConversation(updatedConversation)
  } else {
    console.log(
      "updateConversationFromStartMessage: Creating new conversation from start_conversation event",
    )

    const now = new Date().toISOString()
    const sponsorId = getCurrentSponsor()
    if (!sponsorId) {
      console.error("updateConversationFromStartMessage: Missing sponsorId")
      return
    }

    // Preserve any messages already appended to a temp placeholder conversation (is_temp: true).
    // With suppress_first_message flows (e.g. video taps, C2-2210), the backend can deliver a
    // PlayVideo word-done message before — or concurrently with — the start_conversation
    // confirmation. Those messages land in the temp conversation's transcript. If we reset
    // transcript to [] here, they vanish and the thumbnail flickers out ~1 s after appearing.
    // For non-temp outgoing conversations (mid-conversation sidekick transfers), we still start
    // fresh because the real conversation history arrives separately via the REST sync.
    const inheritedTranscript = currentConversation?.is_temp
      ? (currentConversation.transcript ?? [])
      : []

    const newConversation: TConversation = {
      id: conversationId,
      sidekick_avatar_id: sidekickAvatarId,
      sidekick_name: sidekickName,
      transcript: inheritedTranscript,
      started_at: now,
      status: "active",
      system_prompt: "",
      user_id: "",
      llm_metadata: [],
      // FIXME: The websocket's esk_sponsor field is not reliable yet. Once the backend
      // returns correct values, we should use eskSponsor from the message instead of
      // getCurrentSponsor(), as its atomic and immediately available.
      active_esk_sponsor: sponsorId,
      active_esk_type: eskType,
      active_task_id: taskId,
    }

    setCurrentConversation(newConversation)
  }
}

function isCancelledError(e: unknown): boolean {
  if (e == null || typeof e !== "object") return false
  const o = e as { name?: string; message?: string }
  return o.name === "CancelledError" || o.message === "CancelledError"
}

// On web, removeQueries can trigger CancelledError from in-flight fetches (sync throw or promise rejection).
// Absorb it so channel switch / clear doesn't surface that error.
// On React Native (e.g. Android), window may exist but addEventListener is not a function.
function removeConversationQueriesSafely(queryClient: QueryClient) {
  const remove = () => queryClient.removeQueries({ queryKey: ["conversations"] })
  const canUseUnhandledRejection =
    typeof window !== "undefined" && typeof window.addEventListener === "function"

  try {
    if (canUseUnhandledRejection) {
      const handler = (event: PromiseRejectionEvent) => {
        if (isCancelledError(event?.reason)) {
          event.preventDefault()
          event.stopImmediatePropagation()
        }
      }
      window.addEventListener("unhandledrejection", handler)
      setTimeout(() => window.removeEventListener("unhandledrejection", handler), 100)
    }
    remove()
  } catch (e) {
    if (!isCancelledError(e)) throw e
  }
}

// Hook to clear all conversation history
export function useClearCachedConversations() {
  const { setCurrentConversation } = useCurrentConversationMmkv()
  const { setPastConversations } = usePastConversations()
  const { clearChatHistoryDiagnostic } = useChatHistoryDiagnostic()
  const queryClient = useQueryClient()
  const { typingStore } = useStores()

  const clearCachedConversations = React.useCallback(() => {
    setCurrentConversation(undefined)
    setPastConversations(undefined)
    setRecentlyClosedConversations([])
    // TODO: CHAT_LAST_CONVERSATION_KEY can be removed in a future release now that the recentlyClosedConversations list is used instead of the lastConversation key.  This line is only here to clean up local storaget if the key exists.
    storage.delete(CHAT_LAST_CONVERSATION_KEY)
    removeConversationQueriesSafely(queryClient)
    // Clear any partial streaming messages from the typing store
    typingStore.resetString()
    clearChatHistoryDiagnostic()
    setSynchronizeAllConversationsFromQueryStatus(false)
    // Mark that we need to sync after clearing cache
    setNeedsSyncStatus(true)
  }, [
    setCurrentConversation,
    setPastConversations,
    queryClient,
    typingStore,
    clearChatHistoryDiagnostic,
  ])

  return clearCachedConversations
}

// Clear flags related to conversation lifecycle
export function useClearConversationStatusFlags() {
  const queryClient = useQueryClient()

  return React.useCallback(() => {
    setSynchronizeAllConversationsFromQueryStatus(false)
    // Mark that we need to sync if we have cleared our status flags
    // We don't know what state we were left in, so the safe thing to do is sync.
    setNeedsSyncStatus(true)
    queryClient.setQueryData(["startConversationStatus"], { isPending: false })
    queryClient.setQueryData(["closeConversationStatus"], { isPending: false })
    queryClient.setQueryData(["conversationRejectedStatus"], { isRejected: false })
  }, [queryClient])
}

// Gets the current conversation from MMKV storage
// Used as an alternative to hooks, for example inside callbacks or event listeners
export function getCurrentConversation(): TConversation | undefined {
  const conversationStr = storage.getString(CHAT_CURRENT_CONVERSATION_KEY)
  if (!conversationStr) return undefined

  return JSON.parse(conversationStr) as TConversation
}

// ---------------------------------------------------------------------------
// Web localStorage quota helpers
// localStorage has a ~5MB quota. These constants and helpers are shared by all
// storage-write functions below. Native MMKV writes to disk with no meaningful
// size limit so all guards are no-ops on native.
// ---------------------------------------------------------------------------

/**
 * Maximum number of past conversations to persist.
 * Server sync restores the full history on the next active session.
 */
const MAX_CACHED_PAST_CONVERSATIONS = 25

/**
 * Maximum transcript messages kept when the current conversation is too large
 * to fit in localStorage even after evicting past conversations.
 */
const MAX_CURRENT_CONVERSATION_TRANSCRIPT_MESSAGES = 150

function isQuotaExceededError(e: unknown): boolean {
  if (typeof DOMException !== "undefined" && e instanceof DOMException) {
    return e.name === "QuotaExceededError" || e.code === 22
  }
  return (
    e != null &&
    typeof e === "object" &&
    ((e as { name?: string }).name === "QuotaExceededError" ||
      (e as { message?: string }).message?.includes("quota") === true)
  )
}

// ---------------------------------------------------------------------------

export function setCurrentConversation(conversation: TConversation | undefined) {
  if (conversation === undefined) {
    storage.delete(CHAT_CURRENT_CONVERSATION_KEY)
    return
  }

  try {
    storage.set(CHAT_CURRENT_CONVERSATION_KEY, JSON.stringify(conversation))
  } catch (e) {
    if (!isQuotaExceededError(e) || Platform.OS !== "web") throw e

    // Evict past conversations (server-recoverable) to free space, then retry
    storage.delete(CHAT_PAST_CONVERSATIONS_KEY)
    try {
      storage.set(CHAT_CURRENT_CONVERSATION_KEY, JSON.stringify(conversation))
      return
    } catch {
      // Still too large — truncate the transcript to the most recent messages
    }

    const truncated: TConversation = {
      ...conversation,
      transcript: conversation.transcript.slice(-MAX_CURRENT_CONVERSATION_TRANSCRIPT_MESSAGES),
    }
    try {
      storage.set(CHAT_CURRENT_CONVERSATION_KEY, JSON.stringify(truncated))
      Sentry.captureMessage("setCurrentConversation: quota exceeded, stored truncated transcript", {
        level: "warning",
        extra: {
          originalLength: conversation.transcript.length,
          storedLength: truncated.transcript.length,
        },
      })
    } catch {
      storage.delete(CHAT_CURRENT_CONVERSATION_KEY)
      Sentry.captureMessage(
        "setCurrentConversation: quota exceeded even for truncated conversation, cleared cache",
        {
          level: "error",
          extra: { transcriptLength: conversation.transcript.length },
        },
      )
    }
  }
}

export function setRecentlyClosedConversations(conversations: TConversation[]) {
  if (conversations.length === 0) {
    storage.delete(CHAT_RECENTLY_CLOSED_CONVERSATIONS_KEY)
    return
  }

  try {
    storage.set(CHAT_RECENTLY_CLOSED_CONVERSATIONS_KEY, JSON.stringify(conversations))
  } catch (e) {
    if (!isQuotaExceededError(e) || Platform.OS !== "web") throw e

    // Progressive fallback: halve the list until it fits
    let limit = Math.floor(conversations.length / 2)
    while (limit >= 1) {
      try {
        storage.set(
          CHAT_RECENTLY_CLOSED_CONVERSATIONS_KEY,
          JSON.stringify(conversations.slice(0, limit)),
        )
        return
      } catch {
        limit = Math.floor(limit / 2)
      }
    }
    storage.delete(CHAT_RECENTLY_CLOSED_CONVERSATIONS_KEY)
  }
}

export function setPastConversations(conversations: TConversation[] | undefined) {
  if (conversations === undefined) {
    storage.delete(CHAT_PAST_CONVERSATIONS_KEY)
    return
  }

  // On web, cap the list to avoid hitting the localStorage quota upfront.
  const toStore =
    Platform.OS === "web" ? conversations.slice(0, MAX_CACHED_PAST_CONVERSATIONS) : conversations

  try {
    storage.set(CHAT_PAST_CONVERSATIONS_KEY, JSON.stringify(toStore))
  } catch (e) {
    if (!isQuotaExceededError(e)) throw e

    // Progressive fallback: keep halving the count until it fits or we give up
    let limit = Math.floor(toStore.length / 2)
    while (limit >= 1) {
      try {
        storage.set(CHAT_PAST_CONVERSATIONS_KEY, JSON.stringify(toStore.slice(0, limit)))
        Sentry.captureMessage("setPastConversations: quota exceeded, stored reduced set", {
          level: "warning",
          extra: { attempted: toStore.length, stored: limit },
        })
        return
      } catch {
        limit = Math.floor(limit / 2)
      }
    }

    // Nothing fits — clear so stale data doesn't block a future write
    storage.delete(CHAT_PAST_CONVERSATIONS_KEY)
    Sentry.captureMessage(
      "setPastConversations: quota exceeded even for 1 conversation, cleared cache",
      {
        level: "error",
        extra: { attempted: toStore.length },
      },
    )
  }
}
